import type { CSSProperties } from "react";
import { PageHero } from "../components/PageShell";
import { Placeholder } from "../components/Placeholder";
import { ROUTES } from "../lib/routes";

interface Metric {
  count: number;
  decimals?: number;
  prefix?: string;
  suffix: string;
  lbl: string;
  delta: string;
  arrow: string;
  dir: string;
}

const metrics: Metric[] = [
  { count: 62, suffix: "%", lbl: "Solar share of grid load", delta: "vs 28% in 2020", arrow: "↑", dir: "up" },
  { count: 1.4, decimals: 1, suffix: "MW", lbl: "Rooftop solar installed", delta: "+ 0.6 MW in 2024-25", arrow: "↑", dir: "up" },
  { count: 41, prefix: "− ", suffix: "%", lbl: "Per-capita water use", delta: "vs 2018 baseline", arrow: "↓", dir: "down" },
  { count: 92, suffix: "%", lbl: "Waste segregated at source", delta: "vs 54% in 2020", arrow: "↑", dir: "up" },
];

const pillars = [
  { num: "01", title: "Energy", desc: "Solar-first electricity, LED retrofit across all 21 blocks, smart-metering on every floor, and a heat-recovery campaign for the print labs.", items: ["1.4 MW rooftop solar (2025)", "100% LED · all academic blocks", "Smart meters · every floor", "Net-zero by 2030 commitment"] },
  { num: "02", title: "Water", desc: "Five rainwater-harvesting tanks (combined 4.2 lakh litres), drip irrigation across 8 acres, dual-flush retrofits, and the no-bottled-water policy in offices.", items: ["4.2 lakh L harvesting capacity", "Drip irrigation · 8 acres", "STP · 80 KLD capacity", "Bottle-free admin since 2022"] },
  { num: "03", title: "Waste", desc: "Six-stream segregation at source. In-vessel composting handles all wet waste; e-waste collected through authorised CPCB recyclers.", items: ["6-stream segregation", "Composting · 80 kg/day", "CPCB-authorised e-waste", "Zero-waste-to-landfill goal · 2027"] },
  { num: "04", title: "Mobility", desc: "Two campus EV shuttles, secure parking for 800 cycles, and a BIET-DVG city-bus pass scheme negotiated with KSRTC.", items: ["2 EV shuttles · 28 trips/day", "800-cycle parking", "Subsidised KSRTC pass", "EV charging · 12 points"] },
  { num: "05", title: "Biodiversity", desc: "1,400 trees catalogued in the annual census; 4 acre miyawaki forest planted in 2023; bee-keeping pilot in the agriculture-engineering plot.", items: ["1,400 trees · annual census", "4 acre Miyawaki forest", "Pollinator garden · 0.5 ac", "12 bird species year-round"] },
  { num: "06", title: "Curriculum", desc: "Sustainability is a graded module in three of nine UG branches. Final-year SDG-aligned projects compulsory across all departments.", items: ["Module in CSE, ME, CE", "All FY projects · SDG mapped", "Workshop · annual sustain·BIET", "Faculty cohort · 14 active"] },
];

const sdgs = [
  { num: 4, name: "Quality Education", active: true, cls: "" },
  { num: 5, name: "Gender Equality", active: true, cls: "" },
  { num: 6, name: "Clean Water & Sanitation", active: true, cls: "water" },
  { num: 7, name: "Affordable & Clean Energy", active: true, cls: "energy" },
  { num: 9, name: "Industry & Innovation", active: true, cls: "industry" },
  { num: 10, name: "Reduced Inequalities", active: false, cls: "" },
  { num: 11, name: "Sustainable Cities", active: true, cls: "cities" },
  { num: 12, name: "Responsible Consumption", active: true, cls: "consumption" },
  { num: 13, name: "Climate Action", active: true, cls: "" },
  { num: 14, name: "Life Below Water", active: false, cls: "" },
  { num: 15, name: "Life on Land", active: true, cls: "" },
  { num: 17, name: "Partnerships for Goals", active: true, cls: "" },
];

export default function Sustainability() {
  return (
    <>
      <PageHero
        variant="hero-photo"
        eyebrow="Sustainability"
        title="Green Campus Report 2024-25"
        lead="An autonomous institution should be able to account for what it consumes. This is our open ledger — energy, water, waste, mobility, biodiversity, curriculum — refreshed each November after the audit cycle. Reviewed by the Internal Quality Assurance Cell."
        crumbs={[{ label: "About", to: ROUTES.about }, { label: "Sustainability" }]}
        image={null}
      />
      <section className="section">
        <div className="container">
          <div className="solar-hero reveal">
            <div className="solar-hero-copy">
              <div className="kicker">Net-zero commitment · 2030</div>
              <h2>62% of our grid load<br />is now solar.</h2>
              <p>In 2020 we ran on 28% solar. In 2025, after the third rooftop expansion, that's 62% — and the campus runs net-zero on weekday afternoons through most of the year. The remaining gap is in lab-equipment cycles and HVAC, both queued for the 2026-27 phase.</p>
              <div className="solar-hero-meta">
                <div><b data-count="1.4" data-decimals="1" data-suffix=" MW">0 MW</b>Installed</div>
                <div><b data-count="1820" data-suffix=" t">0 t</b>CO&#8322; avoided / year</div>
                <div><b data-count="2030" data-format="plain">0</b>Net-zero target</div>
              </div>
            </div>
            <div className="solar-bar-col">
              <div
                className="solar-bar"
                style={{ ["--solar-target" as string]: "62%" } as CSSProperties}
                ref={(el: HTMLDivElement | null) => {
                  if (!el || (el as any).dataset._wired) return;
                  (el as any).dataset._wired = "1";
                  const io = new IntersectionObserver((es) => {
                    es.forEach((e) => { if (e.isIntersecting) { el.classList.add("in"); io.disconnect(); } });
                  }, { threshold: 0.3 });
                  io.observe(el);
                }}
              >
                <div className="solar-bar-fill"></div>
                <div className="solar-bar-ticks">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>
              <div className="solar-bar-label">
                <div className="num"><span data-count="62" data-suffix="%">28%</span></div>
                <div className="lbl">Solar share &middot; 2025</div>
              </div>
            </div>
          </div>

          <div className="sustain-metrics">
            {metrics.map((m, i) => (
              <div key={i} className="sustain-metric reveal">
                <div className={"arrow " + m.dir}>{m.arrow}</div>
                <div className="num">
                  <span
                    data-count={m.count}
                    {...(m.prefix ? { "data-prefix": m.prefix } : {})}
                    {...(m.decimals != null ? { "data-decimals": m.decimals } : {})}
                  >0</span>
                  <span>{m.suffix}</span>
                </div>
                <div className="lbl">{m.lbl}</div>
                <div className="delta">{m.delta}</div>
              </div>
            ))}
          </div>

          <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>Six pillars</h2>
          <p style={{ color: "var(--text-soft)", marginTop: 8, fontSize: 17, maxWidth: "60ch" }}>Programs are organized along the six dimensions audited under the AICTE Clean &amp; Smart Campus directive.</p>
          <div className="pillars-grid">
            {pillars.map((p, i) => (
              <div key={i} className="pillar reveal">
                <div className="num">{p.num}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <ul>
                  {p.items.map((x, j) => <li key={j}>{x}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>SDG mapping</h2>
          <p style={{ color: "var(--text-soft)", marginTop: 8, fontSize: 17, maxWidth: "60ch" }}>Active alignment with 9 of the UN Sustainable Development Goals. We map every research project and curriculum module to at least one SDG.</p>
          <div className="sdg-grid reveal">
            {sdgs.map((s, i) => (
              <div key={i} className={"sdg-tile " + (s.active ? "active " + s.cls : "inactive")}>
                <div className="sdg-num">{s.num}</div>
                <div className="sdg-name">{s.name}</div>
              </div>
            ))}
          </div>

          <div className="two-col" style={{ marginTop: 80 }}>
            <div className="reveal">
              <div className="kicker">Audits & verification</div>
              <h3>Open ledger.</h3>
              <p>The IQAC publishes the green-audit summary each November. Carbon, water and waste numbers are independently verified by an external auditor every two years; the latest verification was in October 2024.</p>
              <p style={{ marginTop: 16 }}>Raw data — meter readings, water flows, waste tonnage — is available on request to researchers and journalists.</p>
              <div style={{ display: "flex", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
                <a className="btn btn-primary" href="#">Download report PDF →</a>
                <a className="btn btn-ghost" href="mailto:greencampus@bietdvg.edu">Request raw data →</a>
              </div>
            </div>
            <div className="reveal">
              <Placeholder label="MIYAWAKI FOREST · YEAR 2" aspect="4/5" />
            </div>
          </div>

          <div className="contact-grid" style={{ marginTop: 80 }}>
            <div className="contact-block">
              <div className="ctype">Office</div>
              <h3>Green Campus Cell</h3>
              <dl className="row"><dt>Convenor</dt><dd>Dr. Faculty-30</dd><dt>Phone</dt><dd>+91 8192 233 815</dd><dt>Email</dt><dd><a href="mailto:greencampus@bietdvg.edu">greencampus@bietdvg.edu</a></dd></dl>
            </div>
            <div className="contact-block">
              <div className="ctype">IQAC</div>
              <h3>Internal Quality Assurance Cell</h3>
              <dl className="row"><dt>Coordinator</dt><dd>Dr. Faculty-07</dd><dt>Email</dt><dd><a href="mailto:iqac@bietdvg.edu">iqac@bietdvg.edu</a></dd></dl>
            </div>
            <div className="contact-block">
              <div className="ctype">Student forum</div>
              <h3>Green Campus Forum</h3>
              <dl className="row"><dt>Lead</dt><dd>Student council</dd><dt>Email</dt><dd><a href="mailto:greenforum@bietdvg.edu">greenforum@bietdvg.edu</a></dd></dl>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
